import React, { lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { UserProvider, useUser } from './context/UserContext';
import { RegistrationProvider } from './context/RegistrationContext';
import Loading from './pages/components/loading';
import './App.css';
import Logo from './pages/components/708090.png';
import GDPR from './pages/GDPR';
import PrintableGRN from './pages/PrintableGRN';
import PrintableMIS from './pages/PrintableMIS';
import PrintableInvoice from './pages/PrintableInvoice';
import PrintableEstimate from './pages/PrintableEstimate';
import PrintableEntrySheet from './pages/PrintableEntrySheet';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import DriverPage from './pages/driver';
import './i18n';
import PWAContext from './components/PWAContext';

// Convert regular imports to lazy imports
const LoginPage = lazy(() => import('./pages/loginPage'));
const DashboardPage = lazy(() => import('./pages/dashboardPage'));
const PricingPage = lazy(() => import('./pages/pricingPage'));
const PaymentRecovery = lazy(() => import('./pages/PaymentRecovery'));
// const EndUser = lazy(() => import('./pages/endUser'));
const EmployeeDashboardPage = lazy(() => import('./pages/employeeDashboardPage'));
const Review = lazy(() => import('./pages/review'));
// const NotificationPage = lazy(() => import('./pages/NotificationPage'));
const CancelledSubscription = lazy(() =>
  import('./pages/CancelledSubscription')
);

const DriverPageWithTitle = () => {
  return (
    <>
      <Helmet>
        <title>Șofer | Servicegest</title>
      </Helmet>
      <DriverPage />
    </>
  );
};
const CancelledSubscriptionWithTitle = () => {
  return (
    <>
      <Helmet>
        <title>Abonament anulat | Servicegest</title>
      </Helmet>
      <CancelledSubscription />
    </>
  );
};
const DashboardPageWithTitle = () => {
  return (
    <>
      <Helmet>
        <title>Dashboard | Servicegest</title>
      </Helmet>
      <DashboardPage />
    </>
  );
};
const LoginPageWithTitle = () => {
  return (
    <>
      <Helmet>
        <title>Autentificare | Servicegest</title>
      </Helmet>
      <LoginPage />
    </>
  );
};

const PricingPageWithTitle = () => {
  return (
    <>
      <Helmet>
        <title>Prețuri | Servicegest</title>
        <meta
          name="description"
          content="Descoperă prețurile competitive pentru serviciile oferite de Servicegest, inclusiv ITP, RCA, CASCO, și ROVINIETA. Economisește timp și bani cu cele mai bune oferte."
        />
        <meta property="og:title" content="Prețuri | Servicegest" />
        <meta property="og:image" content={Logo} />
        <meta property="og:type" content="website" />
        <meta
          property="og:description"
          content="Descoperă prețurile competitive pentru serviciile oferite de Servicegest, inclusiv ITP, RCA, CASCO, și ROVINIETA. Economisește timp și bani cu cele mai bune oferte."
        />
        <meta property="og:locale" content="ro_RO" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <PricingPage />
    </>
  );
};

const EmployeeDashboardPageWithTitle = () => {
  return (
    <>
      <Helmet>
        <title>Dashboard Angajat | Servicegest</title>
      </Helmet>
      <EmployeeDashboardPage />
    </>
  );
};

const AppContent = () => {
  const { loading, user, userRole, userPermissions, paymentStatus, error } = useUser();

  const hasAccessToDashboard = (role) => {
    const allowedRoles = ['AUTO_SHOP', 'ADMIN', 'AUTO_SHOP_DRIVER', 'DEMO'];
    return allowedRoles.includes(role);
  };

  const hasAccessToDriverDashboard = (role) => {
    const allowedRoles = ['DRIVER', 'AUTO_SHOP_DRIVER'];
    return allowedRoles.includes(role);
  };

  const hasPackage3Access = () => {
    return userRole === 'DEMO' || userPermissions === 'PACKAGE_3' || userPermissions === 'FULL';
  };

  // Check if user has valid payment status (PAID or PAST_DUE)
  const hasValidPaymentStatus = () => {
    // Allow access for DEMO users, drivers, or users with PAID/PAST_DUE status
    if (userRole === 'DEMO' || userRole === 'DRIVER' || userRole === 'ADMIN') return true;
    // ONGOING is for new registrations going through billing setup
    if (paymentStatus === 'ONGOING') return false;
    return paymentStatus === 'PAID' || paymentStatus === 'PAST_DUE';
  };

  // Check if user needs to complete payment recovery (FAILED status)
  const needsPaymentRecovery = () => {
    console.log('needsPaymentRecovery check:', { userRole, paymentStatus });
    if (userRole === 'DEMO' || userRole === 'DRIVER' || userRole === 'ADMIN') return false;
    return paymentStatus === 'FAILED';
  };

  // Get login context to determine redirect for AUTO_SHOP_DRIVER
  const getLoginContext = () => {
    return localStorage.getItem('loginContext') || 'service';
  };

  if (loading) {
    return <Loading />;
  }

  // If there's an error fetching user role and user is authenticated, show error page
  if (error && user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Eroare la încărcarea rolului utilizatorului
          </h2>
          <p className="text-gray-600 mb-4">
            Te rugăm să încerci din nou sau să contactezi suportul.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Încearcă din nou
          </button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={<LoginPageWithTitle />} />
          <Route path="/pricing" element={
            user && needsPaymentRecovery() ? (
              <Navigate to="/payment-recovery" replace />
            ) : (
              <PricingPageWithTitle />
            )
          } />
          <Route path="/payment-recovery" element={
            user ? <PaymentRecovery /> : <Navigate to="/login" replace />
          } />
          <Route path="/cancelled" element={<CancelledSubscriptionWithTitle />} />
          <Route path="/review" element={<Review />} />
          <Route path="/loading" element={<Loading />} />
          <Route path="/gdpr" element={<GDPR />} />
          <Route path="/gdpr/:shopId" element={<GDPR />} />
          
          {/* Print routes - only accessible to PACKAGE_3 and FULL users */}
          {user && hasPackage3Access() && hasValidPaymentStatus() && (
            <>
              <Route path="/print-nir" element={<PrintableGRN />} />
              <Route path="/print-bon" element={<PrintableMIS />} /> 
              <Route path="/print-factura" element={<PrintableInvoice />} />
              <Route path="/print-deviz" element={<PrintableEstimate />} />
              <Route path="/print-foaie-intrare" element={<PrintableEntrySheet />} />
            </>
          )}
          
          {/* Driver dashboard routes */}
          <Route path="/driver/*" element={
              user ? (
                hasAccessToDriverDashboard(userRole) ? (
                  <DriverPageWithTitle />
                ) : (
                  <Navigate to="/pricing" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          
          {/* Auto shop dashboard routes */}
          <Route path="/dashboard/*" element={
              user ? (
                needsPaymentRecovery() ? (
                  <Navigate to="/payment-recovery" replace />
                ) : hasAccessToDashboard(userRole) && hasValidPaymentStatus() ? (
                  <DashboardPageWithTitle />
                ) : paymentStatus === 'ONGOING' ? (
                  <Navigate to="/pricing" replace />
                ) : (
                  <Navigate to="/pricing" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/employee/*" element={
              user ? (
                needsPaymentRecovery() ? (
                  <Navigate to="/payment-recovery" replace />
                ) : (
                  <EmployeeDashboardPageWithTitle />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          
          {/* Root route - redirect based on user role, payment status, and login context */}
          <Route path="/" element={
            user ? (
              needsPaymentRecovery() ? (
                <Navigate to="/payment-recovery" replace />
              ) : userRole === 'DRIVER' ? (
                <Navigate to="/driver" replace />
              ) : userRole === 'AUTO_SHOP_DRIVER' ? (
                getLoginContext() === 'driver' ? (
                  <Navigate to="/driver" replace />
                ) : hasValidPaymentStatus() ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Navigate to="/pricing" replace />
                )
              ) : hasAccessToDashboard(userRole) && hasValidPaymentStatus() ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/pricing" replace />
              )
            ) : (
              <LoginPageWithTitle />
            )
          } />
          
          <Route path="/*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <UserProvider>
        <RegistrationProvider>
          <PWAContext>
            <AppContent />
            <ToastContainer position="bottom-center" />
          </PWAContext>
        </RegistrationProvider>
      </UserProvider>
    </HelmetProvider>
  );
};

export default App;
